import React, { useEffect, useState } from 'react';
import { ClipboardCopy, Loader2, MessageSquareText, Save } from 'lucide-react';
import { getStaffFundraiserSettings, saveFundraiserSettings } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const isIOS = () => /iPad|iPhone|iPod/.test(window.navigator.userAgent);
export const groupSmsHref = (phones, body) => {
  const numbers = phones.map((p) => p.replace(/[^\d+]/g, '')).filter(Boolean);
  const text = encodeURIComponent(body);
  if (isIOS()) return `sms:/open?addresses=${numbers.join(',')}&body=${text}`;
  return `sms:${numbers.join(',')}?body=${text}`;
};

export const RsvpReminderPanel = ({ slug, rows }) => {
  const { toast } = useToast();
  const [settings, setSettings] = useState(null);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSettings(null);
    getStaffFundraiserSettings(slug).then((data) => { setSettings(data); setText(data.reminderText || ''); }).catch(() => setSettings(null));
  }, [slug]);

  if (!settings) return null;
  const phones = rows.map((r) => r.phone).filter((p) => p && p.replace(/\D/g, '').length >= 7);
  const dirty = text !== (settings.reminderText || '');

  const save = async () => {
    setSaving(true);
    try { const saved = await saveFundraiserSettings(slug, { ...settings, reminderText: text }); setSettings(saved); toast({ title: 'Reminder text saved' }); } catch (e) { toast({ title: 'Save failed', description: e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || e.message }); } finally { setSaving(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); toast({ title: 'Reminder copied' }); } catch { toast({ title: 'Could not copy' }); }
  };

  return (
    <div className="bg-white border border-[#0a1f44]/10 p-5 space-y-3" data-testid="rsvp-reminder-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div><div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Morning-of reminder</div><div className="font-display text-xl text-[#0a1f44]">TEXT EVERYONE WHO RSVP'D</div></div>
        <span className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/60" data-testid="rsvp-reminder-recipients">{phones.length} of {rows.length} {rows.length === 1 ? 'family has' : 'families have'} a phone number</span>
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={600} rows={4} data-testid="rsvp-reminder-text" className="w-full border border-[#0a1f44]/20 px-3 py-2 text-sm text-[#0a1f44] focus:outline-none focus:border-[#c8102e]" />
      <div className="flex flex-wrap gap-2">
        <a href={phones.length ? groupSmsHref(phones, text) : undefined} aria-disabled={!phones.length} data-testid="rsvp-reminder-sms" className={`inline-flex items-center gap-2 font-heading uppercase tracking-widest text-[10px] px-4 py-2.5 transition-colors ${phones.length ? 'bg-[#c8102e] hover:bg-[#e11d34] text-white' : 'bg-[#0a1f44]/10 text-[#0a1f44]/40 pointer-events-none'}`}><MessageSquareText className="w-4 h-4" /> Text {phones.length} {phones.length === 1 ? 'family' : 'families'}</a>
        <button type="button" onClick={copy} data-testid="rsvp-reminder-copy" className="inline-flex items-center gap-2 border border-[#0a1f44]/30 text-[#0a1f44] hover:bg-[#0a1f44] hover:text-white font-heading uppercase tracking-widest text-[10px] px-4 py-2.5 transition-colors"><ClipboardCopy className="w-4 h-4" /> Copy Message</button>
        <button type="button" onClick={save} disabled={!dirty || saving} data-testid="rsvp-reminder-save" className="ml-auto inline-flex items-center gap-2 bg-[#0a1f44] disabled:opacity-40 hover:bg-[#c8102e] text-white font-heading uppercase tracking-widest text-[10px] px-4 py-2.5 transition-colors">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save Text</button>
      </div>
    </div>
  );
};
